import { createSlice } from '@reduxjs/toolkit';
import {
  activateAccountThunk,
  deactivateAccountThunk,
  editProfileThunk,
  fetchUserDetailThunk,
  loginThunk,
  logoutThunk,
  resetPasswordThunk,
  sendEmailForResettingPasswordThunk,
  signupThunk,
  updatePasswordThunk
} from '../thunks';
import { AppState, CurrentUser } from '../../params/redux';

// Initial state with default values for authentication and user info
const initialState: AppState = {
  // Auth token stored after successful login
  token: '',
  // User's detail object
  currentUser: {
    id: 0,
    client_id: 0,
    role: null,
    name: '',
    email: '',
    agency_name: '',
    plan_id: null,
    plan_name: null,
    first_name: '',
    last_name: '',
    phone_number: '',
    status: 'inactive',
    agency_id: 0,
    is_primary: 0,
    last_login: '',
    stripe_customer_id: '',
    billing_enabled: false,
    profile_picture: null,
    created_at: '',
    updated_at: '',
    middle_name: null,
    state: null,
    country: null,
    address: null,
    zip_code: null,
    subscription_status: 'active',
    external_api_token: null,
    special_link: ''
  },
  selectedToggles: [
    'dashboard',
    'task-files',
    'billing',
    'sales-marketing',
    'lead-management',
    'team-space',
    'customer-management',
    'review-management',
    'calendar',
    'integration'
  ],
  isSales: false,
  flags: {
    isEditing: false,
    isUpdatingPassword: false
  },
  // Flag for loading
  isLoading: false,
  // Flag for logging out
  isLoggingOut: false,
  // Version of the state to track changes
  stateVersion: '1.0.0'
};

// Slice for app-related states and actions
const appSlice = createSlice({
  // Slice name
  name: 'app',
  // inital state for the slice
  initialState,
  reducers: {
    // Replaces the token in the state with a new token
    replaceToken: (state, action) => {
      state.token = action.payload;
    },
    replaceToggleState: (state, action) => {
      state.selectedToggles = action.payload;
    },
    settingBillingEnabledFlag: (state, action) => {
      if (state.currentUser) {
        state.currentUser.billing_enabled = action.payload;
      }
    },
    setIsSales: (state, action) => {
      state.isSales = action.payload;
    },
    settingFlagDetails: (state, action) => {
      const { isEditing, isUpdatingPassword } = action.payload;
      state.flags.isEditing = isEditing;
      state.flags.isUpdatingPassword = isUpdatingPassword;
    },
    settingCurrentUser: (state, action) => {
      state.currentUser = action.payload;
    },
    // Updates the state version (useful for handling migrations)
    setStateVersion: (state, action) => {
      state.stateVersion = action.payload;
    },
    // Resets loading states related to app actions
    resettingAppLoader: (state) => {
      state.isLoading = false;
      state.isLoggingOut = false;
    },
    // Resets all app data to the initial state
    resettingAppData: () => initialState
  },
  extraReducers: (builder) => {
    // Handle async actions for login
    builder
      .addCase(loginThunk.pending, (state) => {
        // Set loading flag during login request
        state.isLoading = true;
      })
      .addCase(loginThunk.fulfilled, (state, action) => {
        // Update state with login success details
        state.token = action.payload?.token as string;
        if (action.payload?.role === 'companyManager') {
          state.isSales = true;
        }
        state.currentUser = {
          ...initialState.currentUser,
          id: action.payload?.userId || 0,
          client_id: action.payload?.client_id || 0,
          role: action.payload?.role || null,
          name: action.payload?.name,
          email: action.payload?.email,
          agency_name: action.payload?.agency_name,
          billing_enabled: action.payload?.billing_enabled || false,
          plan_id: action.payload?.plan_id || null,
          plan_name: action.payload?.plan_name || null

        } as CurrentUser;
      })
      .addCase(loginThunk.rejected, (state) => {
        state.token = '';
        state.currentUser = initialState.currentUser;
      });
    builder
      .addCase(signupThunk.pending, (state) => {
        // Set loading flag during login request
        state.isLoading = true;
      })
      .addCase(signupThunk.fulfilled, (state, action) => {
        state.token = action.payload?.token as string;
        state.currentUser = {
          ...initialState.currentUser,
          id: action.payload?.userId || 0,
          client_id: action.payload?.client_id || 0,
          role: action.payload?.role || null,
          name: action.payload?.name,
          email: action.payload?.email,
          agency_name: action.payload?.agency_name,
          billing_enabled: action.payload?.billing_enabled || false,
          plan_id: action.payload?.plan_id || null,
          plan_name: action.payload?.plan_name || null
        } as CurrentUser;
      })
      .addCase(signupThunk.rejected, (state) => {
        state.token = '';
        state.currentUser = initialState.currentUser;
      });

    // Handle async actions for logout
    builder
      .addCase(logoutThunk.pending, (state) => {
        // Set loading flag during logout request
        state.isLoggingOut = true;
      })
      .addCase(logoutThunk.fulfilled, (state) => {
        // Reset user data and login status on successful logout
        state.token = '';
        state.currentUser = initialState.currentUser;
      })
      .addCase(logoutThunk.rejected, (state) => {
        // Reset user data on logout failure as well
        state.token = '';
        state.currentUser = initialState.currentUser;
      });
    builder
      .addCase(deactivateAccountThunk.pending, (state) => {
        // Set loading flag during logout request
        state.isLoading = true;
      })
      .addCase(deactivateAccountThunk.fulfilled, () => {
        // Reset user data and login status on successful logout
      })
      .addCase(deactivateAccountThunk.rejected, () => {
        // Reset user data on logout failure as well
      });
    builder
      .addCase(activateAccountThunk.pending, (state) => {
        // Set loading flag during logout request
        state.isLoading = true;
      })
      .addCase(activateAccountThunk.fulfilled, () => {
        // Reset user data and login status on successful logout
      })
      .addCase(activateAccountThunk.rejected, () => {
        // Reset user data on logout failure as well
      });
    builder
      .addCase(fetchUserDetailThunk.pending, (state) => {
        // Set loading flag during logout request
        state.isLoading = true;
      })
      .addCase(fetchUserDetailThunk.fulfilled, (state, action) => {
        // Reset user data and login status on successful logout
        const { user, billing_enabled, plan_id, plan_name, subscription_status } = action.payload as {
          user: CurrentUser;
          billing_enabled: boolean;
          plan_id: number;
          plan_name: string;
          subscription_status	: 'trialing' | 'active' | 'past_due';
        };
        state.currentUser = {
          ...user,
          billing_enabled,
          plan_id,
          plan_name,
          subscription_status
        };
      })
      .addCase(fetchUserDetailThunk.rejected, (state) => {
        // Reset user data on logout failure as well
        state.currentUser = initialState.currentUser;
      });
    builder
      .addCase(updatePasswordThunk.pending, (state) => {
        // Set loading flag during logout request
        state.isLoading = true;
      })
      .addCase(updatePasswordThunk.fulfilled, () => {
        // Reset user data and login status on successful logout
      })
      .addCase(updatePasswordThunk.rejected, () => {
        // Reset user data on logout failure as well
      });
    builder
      .addCase(editProfileThunk.pending, (state) => {
        // Set loading flag during logout request
        state.isLoading = true;
      })
      .addCase(editProfileThunk.fulfilled, () => {
        // Reset user data and login status on successful logout
      })
      .addCase(editProfileThunk.rejected, () => {
        // Reset user data on logout failure as well
      });
    builder
      .addCase(sendEmailForResettingPasswordThunk.pending, (state) => {
        // Set loading flag during logout request
        state.isLoading = true;
      })
      .addCase(sendEmailForResettingPasswordThunk.fulfilled, () => {
        // Reset user data and login status on successful logout
      })
      .addCase(sendEmailForResettingPasswordThunk.rejected, () => {
        // Reset user data on logout failure as well
      });
    builder
      .addCase(resetPasswordThunk.pending, (state) => {
        // Set loading flag during logout request
        state.isLoading = true;
      })
      .addCase(resetPasswordThunk.fulfilled, () => {
        // Reset user data and login status on successful logout
      })
      .addCase(resetPasswordThunk.rejected, () => {
        // Reset user data on logout failure as well
      });
  }
});

// Exporting actions for updating app state
export const {
  replaceToggleState,
  settingCurrentUser,
  settingBillingEnabledFlag,
  settingFlagDetails,
  setIsSales,
  setStateVersion,
  replaceToken,
  resettingAppData,
  resettingAppLoader
} = appSlice.actions;

// Exporting the reducer to be used in the store
export default appSlice.reducer;
